import React from 'react';
import type { AlignmentGuides, AlignmentLineGuide, SpacingGuide } from './types';
import {
  GUIDE_COLOR_ALIGNMENT,
  GUIDE_COLOR_HORIZONTAL_EQUAL,
  GUIDE_COLOR_MEASURE,
  GUIDE_COLOR_OVERLAP,
  GUIDE_COLOR_RHYTHM_MATCH,
  GUIDE_COLOR_VERTICAL_EQUAL,
  SPACING_GUIDE_LEGEND,
} from './guideColors';

interface SnapGuidesProps {
  guides: AlignmentGuides;
  active: boolean;
  showGapGuides?: boolean;
}

type SpacingGuideVisual = {
  lineColor: string;
  labelClass: string;
  lineThickness: number;
  labelShape: 'pill' | 'rounded';
  lineStyle: 'solid' | 'dashed';
};

function resolveSpacingGuideVisual(equalGroupId?: string, rhythmMatch?: boolean): SpacingGuideVisual {
  if (!equalGroupId) {
    if (rhythmMatch) {
      return {
        lineColor: GUIDE_COLOR_RHYTHM_MATCH,
        labelClass: 'border-orange-300/80 bg-orange-50 text-stone-900',
        lineThickness: 2,
        labelShape: 'rounded',
        lineStyle: 'dashed',
      };
    }
    return {
      lineColor: GUIDE_COLOR_MEASURE,
      labelClass: 'border-slate-500/90 bg-slate-700 text-slate-100',
      lineThickness: 1,
      labelShape: 'pill',
      lineStyle: 'solid',
    };
  }

  if (equalGroupId === 'equal-y-axis') {
    return {
      lineColor: GUIDE_COLOR_VERTICAL_EQUAL,
      labelClass: 'border-blue-300/80 bg-blue-600 text-white',
      lineThickness: 2,
      labelShape: 'pill',
      lineStyle: 'solid',
    };
  }

  return {
    lineColor: GUIDE_COLOR_HORIZONTAL_EQUAL,
    labelClass: 'border-violet-300/90 bg-violet-600 text-white',
    lineThickness: equalGroupId === 'equal-all' ? 3 : 2,
    labelShape: 'pill',
    lineStyle: 'solid',
  };
}

function spacingLabelRadius(shape: SpacingGuideVisual['labelShape']) {
  return shape === 'rounded' ? 'rounded-md' : 'rounded-full';
}

function renderAlignmentLine(line: AlignmentLineGuide, index: number) {
  if (line.axis === 'x') {
    const height = Math.max(1, line.spanEnd - line.spanStart);
    return (
      <div
        key={`align-x-${index}-${line.position}`}
        className="absolute border-l border-dashed"
        style={{
          left: line.position,
          top: line.spanStart,
          height,
          borderColor: GUIDE_COLOR_ALIGNMENT,
        }}
      />
    );
  }

  const width = Math.max(1, line.spanEnd - line.spanStart);
  return (
    <div
      key={`align-y-${index}-${line.position}`}
      className="absolute border-t border-dashed"
      style={{
        top: line.position,
        left: line.spanStart,
        width,
        borderColor: GUIDE_COLOR_ALIGNMENT,
      }}
    />
  );
}

function isEqualSpacingGuide(guide: SpacingGuide) {
  return Boolean(guide.equalGroupId);
}

function renderSpacingGuide(guide: SpacingGuide, index: number, labelZIndex = 1) {
  const overlapVisual: SpacingGuideVisual = {
    lineColor: GUIDE_COLOR_OVERLAP,
    labelClass: 'border-2 border-dashed border-slate-200 bg-slate-900 text-white ring-2 ring-white/30',
    lineThickness: 2,
    labelShape: 'pill',
    lineStyle: 'dashed',
  };

  const { lineColor, labelClass, lineThickness, labelShape, lineStyle } = guide.overlap
    ? overlapVisual
    : resolveSpacingGuideVisual(guide.equalGroupId, guide.rhythmMatch);

  const lineStyleCss =
    lineStyle === 'dashed'
      ? {
          backgroundImage: `repeating-linear-gradient(to right, ${lineColor} 0, ${lineColor} 4px, transparent 4px, transparent 7px)`,
          backgroundColor: 'transparent',
        }
      : { backgroundColor: lineColor };

  if (guide.axis === 'x') {
    const left = Math.min(guide.start, guide.end);
    const width = Math.abs(guide.end - guide.start);
    const markerTop = Math.min(guide.edgeMarkerStart, guide.edgeMarkerEnd);
    const markerHeight = Math.abs(guide.edgeMarkerEnd - guide.edgeMarkerStart);
    const lineTop = guide.position - markerTop;

    return (
      <React.Fragment key={`spacing-x-${index}-${guide.start}-${guide.end}`}>
        <div className="absolute" style={{ left, top: markerTop }}>
          <div
            className="absolute"
            style={{ left: 0, top: 0, width: lineThickness, height: markerHeight, ...lineStyleCss }}
          />
          <div
            className="absolute"
            style={{
              left: width - lineThickness,
              top: 0,
              width: lineThickness,
              height: markerHeight,
              ...lineStyleCss,
            }}
          />
          <div
            className="absolute"
            style={{
              left: 0,
              top: lineTop,
              width,
              height: lineThickness,
              ...lineStyleCss,
            }}
          />
        </div>
        {guide.labelPosition ? (
          <div
            className={`absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap border px-1.5 py-0.5 text-[10px] font-semibold leading-none shadow-sm ${labelClass} ${spacingLabelRadius(labelShape)}`}
            style={{ left: guide.labelPosition.x, top: guide.labelPosition.y, zIndex: labelZIndex }}
          >
            {guide.label}
          </div>
        ) : null}
      </React.Fragment>
    );
  }

  const top = Math.min(guide.start, guide.end);
  const height = Math.abs(guide.end - guide.start);
  const markerLeft = Math.min(guide.edgeMarkerStart, guide.edgeMarkerEnd);
  const markerWidth = Math.abs(guide.edgeMarkerEnd - guide.edgeMarkerStart);
  const lineLeft = guide.position - markerLeft;
  const verticalLineStyleCss =
    lineStyle === 'dashed'
      ? {
          backgroundImage: `repeating-linear-gradient(to bottom, ${lineColor} 0, ${lineColor} 4px, transparent 4px, transparent 7px)`,
          backgroundColor: 'transparent',
        }
      : { backgroundColor: lineColor };

  return (
    <React.Fragment key={`spacing-y-${index}-${guide.start}-${guide.end}`}>
      <div className="absolute" style={{ left: markerLeft, top }}>
        <div
          className="absolute"
          style={{ left: 0, top: 0, width: markerWidth, height: lineThickness, ...verticalLineStyleCss }}
        />
        <div
          className="absolute"
          style={{
            left: 0,
            top: height - lineThickness,
            width: markerWidth,
            height: lineThickness,
            ...verticalLineStyleCss,
          }}
        />
        <div
          className="absolute"
          style={{
            left: lineLeft,
            top: 0,
            width: lineThickness,
            height,
            ...verticalLineStyleCss,
          }}
        />
      </div>
      {guide.labelPosition ? (
        <div
          className={`absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap border px-1.5 py-0.5 text-[10px] font-semibold leading-none shadow-sm ${labelClass} ${spacingLabelRadius(labelShape)}`}
          style={{ left: guide.labelPosition.x, top: guide.labelPosition.y, zIndex: labelZIndex }}
        >
          {guide.label}
        </div>
      ) : null}
    </React.Fragment>
  );
}

function renderGuideLegend() {
  return (
    <div
      className="absolute bottom-3 left-3 flex flex-col gap-1 rounded-xl border border-slate-300/80 bg-white/95 px-2.5 py-2 text-[10px] text-slate-800 shadow-md backdrop-blur-sm"
      data-builder-guide-legend
    >
      <span className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-500">Gap guides</span>
      {SPACING_GUIDE_LEGEND.map((entry) => (
        <div key={entry.key} className="flex items-center gap-2">
          <span
            className={`inline-block h-2.5 w-2.5 shrink-0 ${entry.shape === 'rounded' ? 'rounded-sm' : 'rounded-full'}`}
            style={{ backgroundColor: entry.swatch }}
          />
          <span>{entry.label}</span>
        </div>
      ))}
    </div>
  );
}

const SnapGuides: React.FC<SnapGuidesProps> = ({ guides, active, showGapGuides = false }) => {
  const alignmentLines = active ? guides.lines ?? [] : [];
  const spacingGuides = active && showGapGuides ? guides.spacing ?? [] : [];
  const baseSpacingGuides = spacingGuides.filter((guide) => !isEqualSpacingGuide(guide));
  const equalSpacingGuides = spacingGuides.filter((guide) => isEqualSpacingGuide(guide));
  const visible = alignmentLines.length > 0 || spacingGuides.length > 0;

  return (
    <div
      className={`pointer-events-none absolute inset-0 z-[10001] transition-opacity duration-150 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      aria-hidden="true"
    >
      <div className="absolute inset-0 z-0">
        {alignmentLines.map((line, index) => renderAlignmentLine(line, index))}
      </div>
      <div className="absolute inset-0 z-[1]">
        {baseSpacingGuides.map((guide, index) => renderSpacingGuide(guide, index, 2))}
      </div>
      <div className="absolute inset-0 z-[2]">
        {equalSpacingGuides.map((guide, index) => renderSpacingGuide(guide, index, 4))}
      </div>
      {active && showGapGuides && spacingGuides.length > 0 ? renderGuideLegend() : null}
    </div>
  );
};

export default React.memo(SnapGuides);
